import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Send, AlertCircle, RefreshCw, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function AdminEmails() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sendingTo, setSendingTo] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [sendNote, setSendNote] = useState("");
  const [inviteEnabled, setInviteEnabled] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [allowlist, setAllowlist] = useState({ allowlist_total: 0, already_invited: 0, pending_invites: [], configured: false });
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [singleEmail, setSingleEmail] = useState("");
  const [singleBusy, setSingleBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/admin/email-previews"),
      api.get("/admin/invite-toggle"),
      api.get("/admin/allowlist/status"),
    ])
      .then(([prev, toggle, status]) => {
        if (!mounted) return;
        setItems(prev.data.items || []);
        setActive((prev.data.items || [])[0] || null);
        setInviteEnabled(!!toggle.data.enabled);
        setAllowlist(status.data);
      })
      .catch((e) => { if (mounted) setErr(e?.response?.data?.detail || "Failed to load"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const refreshAllowlistStatus = async () => {
    try {
      const { data } = await api.get("/admin/allowlist/status");
      setAllowlist(data);
    } catch (e) { /* silent */ }
  };

  const toggleInvite = async () => {
    const next = !inviteEnabled;
    setToggleBusy(true);
    try {
      const { data } = await api.put("/admin/invite-toggle", { enabled: next });
      setInviteEnabled(!!data.enabled);
      toast.success(next ? "Auto invites are ON. New emails added to the sheet will get an invite." : "Auto invites are OFF. Add emails freely without sending anything.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setToggleBusy(false);
    }
  };

  const runSync = async () => {
    setSyncBusy(true);
    setSyncResult(null);
    try {
      const { data } = await api.post("/admin/allowlist/sync");
      setSyncResult(data);
      if (data.sent.length > 0) toast.success(`Sent ${data.sent.length} invite${data.sent.length === 1 ? "" : "s"}`);
      else if (data.skipped_toggle_off.length > 0) toast.info(`Found ${data.skipped_toggle_off.length} new email${data.skipped_toggle_off.length === 1 ? "" : "s"}. Auto invites are OFF, nothing sent.`);
      else if (data.new_found === 0) toast.info("Nothing new. Allowlist is in sync.");
      await refreshAllowlistStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncBusy(false);
    }
  };

  const inviteOne = async () => {
    if (!singleEmail) return;
    setSingleBusy(true);
    try {
      await api.post("/admin/allowlist/invite-one", { email: singleEmail });
      toast.success(`Invite sent to ${singleEmail}`);
      setSingleEmail("");
      await refreshAllowlistStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSingleBusy(false);
    }
  };

  const sendTest = async () => {
    if (!active || !sendingTo) return;
    setSendBusy(true);
    setSendNote("");
    try {
      const { data } = await api.post("/admin/email-previews/send-test", {
        key: active.key,
        to: sendingTo,
      });
      setSendNote(data.ok ? `Sent to ${sendingTo}` : `Failed. ${data.error || "unknown"}`);
    } catch (e) {
      setSendNote(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <div data-testid="admin-emails-page">
      <div className="mb-8">
        <div className="section-tag mb-3"><span className="dot" /> Launch tools</div>
        <h1 className="h-display text-3xl sm:text-4xl flex items-center gap-3">
          <Mail size={28} /> Email Previews
        </h1>
        <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>
          Every transactional email your students receive. Manage student invites and send live tests before you go live.
        </p>
      </div>

      {/* Invite sync controls */}
      {!loading && (
        <div
          className="surface-card p-5 mb-6"
          data-testid="invite-sync-card"
        >
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div className="flex-1 min-w-[280px]">
              <div className="text-xs uppercase tracking-wider font-mono mb-1" style={{ color: "var(--text-dim)" }}>
                Auto invite on allowlist add
              </div>
              <div className="text-base font-semibold mb-1">
                {inviteEnabled
                  ? "ON. New emails added to the sheet get the invite automatically."
                  : "OFF. Add emails to the sheet freely. No one is contacted until you flip this on."}
              </div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                The invite tells the student their spot is ready and links them to {window.location.origin}/login to create their password. Each email is invited only once.
              </div>
            </div>
            <button
              onClick={toggleInvite}
              disabled={toggleBusy}
              className={inviteEnabled ? "btn-ghost text-sm" : "btn-primary text-sm"}
              data-testid="invite-toggle-button"
            >
              {toggleBusy ? "..." : inviteEnabled ? "Turn OFF" : "Turn ON"}
            </button>
          </div>

          {/* Allowlist stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="surface-card p-3" style={{ background: "var(--surface)" }}>
              <div className="text-[10px] uppercase tracking-wider font-mono" style={{ color: "var(--text-dim)" }}>On allowlist</div>
              <div className="text-2xl font-bold mt-1" style={{ color: "var(--lime)" }} data-testid="allowlist-total">{allowlist.allowlist_total}</div>
            </div>
            <div className="surface-card p-3" style={{ background: "var(--surface)" }}>
              <div className="text-[10px] uppercase tracking-wider font-mono" style={{ color: "var(--text-dim)" }}>Already invited</div>
              <div className="text-2xl font-bold mt-1" data-testid="allowlist-invited">{allowlist.already_invited}</div>
            </div>
            <div className="surface-card p-3" style={{ background: "var(--surface)" }}>
              <div className="text-[10px] uppercase tracking-wider font-mono" style={{ color: "var(--text-dim)" }}>Pending invite</div>
              <div className="text-2xl font-bold mt-1" data-testid="allowlist-pending">{allowlist.pending_invites?.length || 0}</div>
            </div>
          </div>

          {/* Pending list preview */}
          {allowlist.pending_invites?.length > 0 && (
            <div className="mb-4 p-3 rounded-2xl" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
              <div className="text-[10px] uppercase tracking-wider font-mono mb-2" style={{ color: "var(--text-dim)" }}>
                Will be invited next sync
              </div>
              <div className="flex flex-wrap gap-1.5">
                {allowlist.pending_invites.slice(0, 20).map((em) => (
                  <span
                    key={em}
                    className="text-xs font-mono px-2 py-1 rounded"
                    style={{ background: "var(--lime-glow)", color: "var(--lime)" }}
                  >
                    {em}
                  </span>
                ))}
                {allowlist.pending_invites.length > 20 && (
                  <span className="text-xs font-mono px-2 py-1" style={{ color: "var(--text-muted)" }}>
                    +{allowlist.pending_invites.length - 20} more
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={runSync}
              disabled={syncBusy || !allowlist.configured}
              className="btn-primary text-sm flex items-center gap-2"
              data-testid="invite-sync-button"
            >
              <RefreshCw size={14} className={syncBusy ? "animate-spin" : ""} />
              {syncBusy ? "Syncing..." : "Sync allowlist now"}
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-[260px]">
              <input
                type="email"
                placeholder="student@gmail.com"
                value={singleEmail}
                onChange={(e) => setSingleEmail(e.target.value)}
                className="input-base flex-1"
                data-testid="invite-single-input"
              />
              <button
                onClick={inviteOne}
                disabled={singleBusy || !singleEmail}
                className="btn-ghost text-sm flex items-center gap-2"
                data-testid="invite-single-button"
              >
                <UserPlus size={14} /> {singleBusy ? "..." : "Invite this one"}
              </button>
            </div>
          </div>

          {syncResult && (
            <div className="text-xs mt-3 font-mono" style={{ color: "var(--text-muted)" }} data-testid="sync-result">
              Allowlist total {syncResult.allowlist_total} &middot; Already invited {syncResult.already_invited} &middot; New found {syncResult.new_found}
              {syncResult.sent.length > 0 && <> &middot; Sent {syncResult.sent.length}</>}
              {syncResult.failed.length > 0 && <> &middot; Failed {syncResult.failed.length}</>}
            </div>
          )}
        </div>
      )}

      {loading && <div className="flex justify-center py-20"><span className="spinner" /></div>}
      {err && (
        <div className="surface-card p-4 mb-6 flex items-start gap-3">
          <AlertCircle size={16} className="mt-0.5" style={{ color: "#ef4444" }} />
          <span className="text-sm">{err}</span>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid lg:grid-cols-[280px,1fr] gap-6" data-testid="email-previews-grid">
          <div className="space-y-1.5">
            {items.map((it) => {
              const isActive = active?.key === it.key;
              return (
                <button
                  key={it.key}
                  onClick={() => { setActive(it); setSendNote(""); }}
                  data-testid={`email-preview-tab-${it.key}`}
                  className="w-full text-left p-3 rounded-2xl transition"
                  style={{
                    background: isActive ? "var(--lime-glow)" : "var(--surface)",
                    border: `1px solid ${isActive ? "var(--border-lime)" : "var(--border)"}`,
                    color: isActive ? "var(--lime)" : "var(--text)",
                  }}
                >
                  <div className="text-sm font-semibold">{it.label}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>To: {it.to}</div>
                </button>
              );
            })}
          </div>

          {active && (
            <div className="space-y-4">
              <div className="surface-card p-5">
                <div className="text-xs uppercase tracking-wider font-mono mb-1" style={{ color: "var(--text-dim)" }}>Subject</div>
                <div className="text-base font-semibold mb-4" data-testid="email-preview-subject">{active.subject}</div>
                <div className="text-xs uppercase tracking-wider font-mono mb-1" style={{ color: "var(--text-dim)" }}>Recipient</div>
                <div className="text-sm" style={{ color: "var(--text-muted)" }}>{active.to}</div>
              </div>

              <div
                className="surface-card overflow-hidden"
                style={{ padding: 0, background: "#0c0c0c" }}
                data-testid="email-preview-frame"
              >
                <iframe
                  title="Email preview"
                  srcDoc={active.html}
                  style={{ width: "100%", height: 720, border: 0, background: "#0c0c0c" }}
                />
              </div>

              <div className="surface-card p-5">
                <div className="text-xs uppercase tracking-wider font-mono mb-2" style={{ color: "var(--text-dim)" }}>Send a test</div>
                <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
                  Sends this exact email via Resend to the inbox you specify. Useful to confirm DKIM and rendering in Gmail or Apple Mail before launch.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={sendingTo}
                    onChange={(e) => setSendingTo(e.target.value)}
                    className="input-base flex-1 min-w-[200px]"
                    data-testid="email-test-input"
                  />
                  <button
                    onClick={sendTest}
                    disabled={sendBusy || !sendingTo}
                    className="btn-primary text-sm flex items-center gap-2"
                    data-testid="email-test-send"
                  >
                    <Send size={14} /> {sendBusy ? "Sending..." : "Send test"}
                  </button>
                </div>
                {sendNote && (
                  <div
                    className="text-xs mt-3 font-mono"
                    style={{ color: sendNote.startsWith("Sent") ? "var(--lime)" : "#ef4444" }}
                    data-testid="email-test-result"
                  >
                    {sendNote}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
